import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePageMeta } from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const passwordRef = useRef(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  usePageMeta("Admin | Connexion", "Connexion sécurisée administrateur");

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      navigate("/admin");
    }
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, {
        username,
        password,
      });
      localStorage.setItem("admin_token", data.access_token);
      navigate("/admin");
    } catch (err) {
      setError("Identifiants invalides. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md bg-white shadow-xl border border-border p-8 space-y-6">
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.4em] text-secondary" data-testid="admin-login-eyebrow">
            Accès sécurisé
          </p>
          <h1 className="text-2xl font-bold text-primary" data-testid="admin-login-title">
            Connexion administrateur
          </h1>
          <p className="text-sm text-muted-foreground" data-testid="admin-login-subtitle">
            Tableau interne de suivi des leads.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
          <div className="space-y-2">
            <label className="text-sm font-semibold" data-testid="admin-login-username-label">
              Identifiant
            </label>
            <Input
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="admin"
              data-testid="admin-login-username-input"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold" data-testid="admin-login-password-label">
              Mot de passe
            </label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="••••••••"
                data-testid="admin-login-password-input"
                ref={passwordRef}
                autoComplete="current-password"
                style={{ WebkitTextSecurity: showPassword ? "none" : "disc" }}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-secondary"
                onClick={() => {
                  setShowPassword((prev) => !prev);
                  passwordRef.current?.focus();
                }}
                data-testid="admin-login-password-toggle"
              >
                {showPassword ? "Masquer" : "Afficher"}
              </button>
            </div>
          </div>
          {error && (
            <p className="text-sm text-red-600" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <Button
            type="submit"
            className="w-full rounded-none uppercase tracking-wider font-bold"
            disabled={loading}
            data-testid="admin-login-submit"
          >
            {loading ? "Connexion..." : "Se connecter"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AdminLoginPage;